/**
 * MethodologyMiniAdvisor — compact, inline version of the Methodology Advisor
 * for embedding at a decision point (e.g. the New Project modal).
 *
 * It reuses the same deterministic engine (methodologyFit.ts) as the full
 * /methodology page, so the recommendation is identical. This inline version is
 * scores-only (the AI narrative lives on the full page) and calls `onApply` with
 * the recommended methodology so the host form can pre-fill its own dropdown — it
 * does not navigate or write to the store.
 */

import { useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import type { Methodology } from '@/types';
import {
  METHODOLOGY_FACTORS,
  computeMethodologyFit,
  defaultMethodologyAnswers,
  type MethodologyAnswers,
} from '@/lib/methodology/methodologyFit';

const METH_TONE: Record<Methodology, string> = {
  Agile: 'bg-brand-orange',
  Waterfall: 'bg-brand-navy',
  Hybrid: 'bg-success-dark',
};

/** Turn a 1–5 answer into a short phrase for display. */
function answerPhrase(low: string, high: string, v: number): string {
  if (v <= 1) return low;
  if (v >= 5) return high;
  if (v === 3) return 'balanced';
  return v === 2 ? `leans "${low}"` : `leans "${high}"`;
}

interface MethodologyMiniAdvisorProps {
  /** Called with the recommended methodology when the user accepts it. */
  onApply: (methodology: Methodology) => void;
  /** Optional: open the full advisor (for the AI explanation). */
  onOpenFull?: () => void;
  className?: string;
}

export function MethodologyMiniAdvisor({
  onApply,
  onOpenFull,
  className,
}: MethodologyMiniAdvisorProps) {
  const [answers, setAnswers] = useState<MethodologyAnswers>(() =>
    defaultMethodologyAnswers(),
  );
  const result = useMemo(() => computeMethodologyFit(answers), [answers]);

  const setAnswer = (key: string, v: number) =>
    setAnswers((prev) => ({ ...prev, [key]: v }));

  const bars = [
    { m: 'Agile' as Methodology, pct: result.scores.agile },
    { m: 'Waterfall' as Methodology, pct: result.scores.waterfall },
    { m: 'Hybrid' as Methodology, pct: result.scores.hybrid },
  ].sort((a, b) => b.pct - a.pct);

  return (
    <div
      className={cn(
        'rounded-lg border border-line-light bg-surface-muted p-3 space-y-3',
        className,
      )}
    >
      <p className="text-[11px] text-ink-tertiary">
        Slide toward whichever end fits best. The recommendation updates live and
        is fully deterministic — the same answers always give the same result.
      </p>

      {/* Factor sliders */}
      <div className="space-y-3">
        {METHODOLOGY_FACTORS.map((f) => (
          <div key={f.key}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-ink-secondary">
                {f.label}
              </span>
              <span className="text-[10px] text-ink-tertiary italic">
                {answerPhrase(f.lowLabel, f.highLabel, answers[f.key] ?? 3)}
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={5}
              step={1}
              value={answers[f.key] ?? 3}
              onChange={(e) => setAnswer(f.key, Number(e.target.value))}
              className="w-full accent-brand-orange"
              aria-label={f.label}
            />
            <div className="flex justify-between text-[10px] text-ink-quaternary">
              <span>{f.lowLabel}</span>
              <span>{f.highLabel}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Live recommendation */}
      <div className="rounded-md border border-line-light bg-white p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-ink-tertiary">Recommended</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-ink-primary">
              {result.recommended}
            </span>
            <Badge
              tone={
                result.confidence === 'high'
                  ? 'success'
                  : result.confidence === 'moderate'
                    ? 'navy'
                    : 'neutral'
              }
              size="sm"
            >
              {result.confidence}
            </Badge>
          </div>
        </div>
        <div className="space-y-1.5">
          {bars.map((b) => (
            <div key={b.m}>
              <div className="flex justify-between text-[11px] mb-0.5">
                <span className="text-ink-secondary">{b.m}</span>
                <span className="tabular-nums font-semibold text-ink-primary">
                  {b.pct}%
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-surface-muted overflow-hidden">
                <div
                  className={cn('h-full', METH_TONE[b.m])}
                  style={{ width: `${b.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="accent"
          size="sm"
          onClick={() => onApply(result.recommended)}
        >
          Use {result.recommended}
        </Button>
        {onOpenFull && (
          <Button type="button" variant="ghost" size="sm" onClick={onOpenFull}>
            Open full advisor
          </Button>
        )}
      </div>
    </div>
  );
}
